import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductImage from "@/components/ProductImage";

export default function FeaturedProducts() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true }).then(setItems).catch(() => setItems([]));
  }, []);

  return (
    <section id="equipment" data-testid="featured-section" className="relative py-24 md:py-32 bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="overline mb-3 text-red-600">// 02 · Featured Hardware</div>
            <h2 className="font-display font-black uppercase text-[#0A1128] text-4xl md:text-5xl tracking-tighter leading-[0.95]">
              Selected for the <span className="text-blue-600">field.</span>
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="see-all-products-link"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#0A1128] hover:text-red-600 transition-colors"
          >
            See full catalog <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {items.map((p, idx) => (
            <Link
              key={p.id}
              to={`/products/${p.slug}`}
              data-testid={`featured-product-${p.slug}`}
              className="group relative bg-white p-6 lift hover:bg-slate-50"
            >
              <div className="flex items-start justify-between mb-5">
                <span className="font-mono-tactical text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                  // {String(idx + 1).padStart(2, "0")} / {p.category_name}
                </span>
                <span className="w-2 h-2 bg-red-600 rounded-full" />
              </div>
              <div className="relative aspect-square w-full bg-slate-50 border border-slate-200 mb-6 flex items-center justify-center overflow-hidden">
                <ProductImage product={p} />
                <span className="absolute -top-px -left-px w-3 h-3 border-t border-l border-red-600" />
                <span className="absolute -top-px -right-px w-3 h-3 border-t border-r border-red-600" />
                <span className="absolute -bottom-px -left-px w-3 h-3 border-b border-l border-red-600" />
                <span className="absolute -bottom-px -right-px w-3 h-3 border-b border-r border-red-600" />
              </div>
              <div className="overline mb-2 text-red-600">{p.tagline}</div>
              <h3 className="font-display font-bold uppercase text-[#0A1128] text-lg tracking-tight leading-tight mb-1">
                {p.name}
              </h3>
              <div className="font-mono-tactical text-[10px] tracking-widest text-slate-500 mb-3">{p.model_number}</div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600 group-hover:text-blue-700">
                Spec sheet <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
